import { createSlice } from '@reduxjs/toolkit';
import { authApi } from '../auth/authApi'
import { IChatboxResponse, IChatResponse } from '../../interfaces/chats/IChatResponse';
import { chatboxApi } from '../chatbox/chatboxApi';


const initialState: {
    topicId: string
    chatTitle: string,
    messages: IChatboxResponse[],
    authMessages: IChatResponse[],
    isSending: boolean,
    error?: string | null
} = {
    topicId: '',
    chatTitle: '',
    messages: [],
    authMessages: [],
    isSending: false,
    error: null,
};

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        setChatId: (state, action) => {
            state.topicId = action.payload;
        },
        setAuthChats: (state, action) => {
            state.authMessages = action.payload;
        },
        setChatMessages: (state, action) => {
            state.messages = action.payload
        },
        removeChatMessage: (state, action) => {
            state.messages = state.messages.filter(message => message.requestId !== action.payload)
        }
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                authApi.endpoints.authWithAgent.matchPending,
                (state, action) => {
                    state.authMessages = action.meta.arg.originalArgs.chatData.map(
                        (arg: IChatResponse, index: number) =>
                            index === action.meta.arg.originalArgs.chatData.length - 1 ?
                                {
                                    user: {
                                        ...arg.user,
                                        requestId: action.meta.requestId,
                                        type: 'pending'
                                    }
                                }
                                : arg);
                    state.isSending = true;
                }
            )
            .addMatcher(
                authApi.endpoints.authWithAgent.matchRejected,
                (state, action) => {
                    state.isSending = false;
                    state.authMessages = state.authMessages.map(
                        (arg: IChatResponse) =>
                            arg.user?.requestId === action.meta.requestId ?
                                {
                                    user: {
                                        ...arg.user,
                                        type: 'error'
                                    }
                                }
                                : arg);
                    const errorObj = action as { payload: { data: { message: string } } };
                    state.error = errorObj.payload.data.message || action.error.message;
                }
            )
            .addMatcher(
                authApi.endpoints.authWithAgent.matchFulfilled,
                (state, action) => {
                    state.isSending = false;
                    if (action.payload.data.metadata) {
                        const { accessToken, refreshToken, isAdmin } = action.payload.data.metadata;
                        if (accessToken) {
                            localStorage.setItem('accessToken', accessToken);
                        }
                        if (refreshToken) {
                            localStorage.setItem('refreshToken', refreshToken);
                        }
                        if (isAdmin) {
                            localStorage?.setItem('roles', 'admins')
                        } else {
                            localStorage?.setItem('roles', 'staff')
                        }
                        localStorage.setItem('user', action?.payload?.data?.metadata?.user?.username || action?.payload?.data?.metadata?.user?.name || action?.payload?.data?.metadata?.user?.email);

                    }
                    state.authMessages = [...action.payload.data.chatData]
                }
            )

            .addMatcher(
                authApi.endpoints.signInWithAgent.matchPending,
                (state, action) => {
                    state.authMessages = action.meta.arg.originalArgs.chatData.map(
                        (arg: IChatResponse, index: number) =>
                            index === action.meta.arg.originalArgs.chatData.length - 1 ?
                                {
                                    user: {
                                        ...arg.user,
                                        requestId: action.meta.requestId,
                                        type: 'pending'
                                    }
                                }
                                : arg);
                    state.isSending = true;
                }
            )
            .addMatcher(
                authApi.endpoints.signInWithAgent.matchRejected,
                (state, action) => {
                    state.isSending = false;
                    state.authMessages = state.authMessages.map(
                        (arg: IChatResponse) =>
                            arg.user?.requestId === action.meta.requestId ?
                                {
                                    user: {
                                        ...arg.user,
                                        type: 'error'
                                    }
                                }
                                : arg);
                    const errorObj = action as { payload: { data: { message: string } } };
                    state.error = errorObj.payload.data.message || action.error.message;
                }
            )
            .addMatcher(
                authApi.endpoints.signInWithAgent.matchFulfilled,
                (state, action) => {
                    state.isSending = false;
                    if (action.payload.data.data.metadata) {
                        const { accessToken, refreshToken, user } = action.payload.data.data.metadata;
                        if (accessToken) {
                            localStorage.setItem('accessToken', accessToken);
                        }
                        if (refreshToken) {
                            localStorage.setItem('refreshToken', refreshToken);
                        }
                        if (user?.type) {
                            localStorage.setItem('roles', user?.type);
                        }
                    }
                    state.authMessages = [...action.payload.data.data.chatData]
                }
            )

            .addMatcher(
                chatboxApi.endpoints.chatbox.matchPending,
                (state, action) => {
                    state.messages.push({
                        _id: action.meta.requestId,
                        message: action.meta.arg.originalArgs.message,
                        requestId: action.meta.requestId,
                        type: 'pending',
                        updatedAt: new Date().toUTCString(),
                        createdAt: new Date().toUTCString(),
                        userId: '',
                        topic: '',
                        user: {
                            name: 'my name'
                        }
                    },
                        {
                            _id: action.meta.requestId,
                            message: '',
                            requestId: action.meta.requestId,
                            type: 'pending',
                            updatedAt: new Date().toUTCString(),
                            createdAt: new Date().toUTCString(),
                            userId: '',
                            topic: '',
                            agent: {
                                name: 'my name'
                            }
                        });
                    state.isSending = true;
                }
            )
            .addMatcher(
                chatboxApi.endpoints.chatbox.matchRejected,
                (state, action) => {
                    state.isSending = false;
                    state.messages = state.messages.filter(message => !(message.requestId === action.meta.requestId && message.agent)).map(
                        (arg: IChatboxResponse) =>
                            arg.requestId === action.meta.requestId ?
                                {
                                    ...arg,
                                    type: 'error'
                                }
                                : arg);
                    state.error = action.error.message;
                }
            )
            .addMatcher(
                chatboxApi.endpoints.chatbox.matchFulfilled,
                (state, action) => {
                    state.isSending = false;
                    const updated = state.messages = state.messages.map(
                        (arg: IChatboxResponse) =>
                            arg.requestId === action.meta.requestId ?
                                arg.user ? {
                                    ...arg,
                                    type: 'success'
                                } : { ...arg, ...action.payload.data, type: 'success', instantReply: true }
                                : arg);
                    state.topicId = action.payload.data.topic
                    state.messages = [...updated] // {...action.payload.data, instantReply: true, agent: {name: 'saga'}}]
                }
            )

    },
});

export const { setChatId, setAuthChats, setChatMessages } = chatSlice.actions;
export default chatSlice.reducer;